// Layout for the millimetre scale the identification key draws ticks on.
//
// Kept pure, apart from the components, so the numbers that decide whether something is
// "drawn at real size" are unit-tested rather than eyeballed. The drawing itself is
// TickShape in src/components/identify/tick-figure.tsx: a 120-unit box with the head at
// the top. These constants describe where the body sits inside that box.

/** Tip of the mouthparts, in TickShape units. */
export const TICK_HEAD_Y = 23;
/** Rear edge of the body. A fed tick swells backwards, so its rear sits lower. */
export const TICK_REAR_Y = 100;
export const TICK_REAR_Y_ENGORGED = 110;
/** Half the leg span from the centre line: the legs reach x = 12 and x = 108. */
export const TICK_HALF_SPAN = 48;

/** Where the scale's zero mark sits inside each row, in CSS px. */
export const SCALE_START_PX = 10;
/** How much ruler every row shows. A fed female is the longest thing on it, at 10–16 mm. */
export const SCALE_MM = 16;
/** Below this many px a tick is a speck, so it gets a magnified inset beside it. */
export const LOUPE_BELOW_PX = 12;
/** How long the magnified drawing should be, roughly, in px. */
export const LOUPE_TARGET_PX = 24;
/** Radius of the inset circle, in px. */
export const LOUPE_RADIUS_PX = 17;
/** Whole-number magnifications, so the label never claims a precision it lacks. */
const MAGNIFICATIONS = [2, 3, 4, 5, 6, 8, 10, 12, 15, 20] as const;

/**
 * The width every row's drawing takes, so the labels beside them line up and the zero
 * marks sit in one column. The floor leaves room for the magnified inset and its label.
 */
export function scaleWidthPx(pxPerMm: number): number {
  return Math.ceil(Math.max(SCALE_START_PX + SCALE_MM * pxPerMm + 18, 100));
}

/** Body length in TickShape units: mouthparts to rear edge. */
export function bodyUnits(engorged: boolean): number {
  return (engorged ? TICK_REAR_Y_ENGORGED : TICK_REAR_Y) - TICK_HEAD_Y;
}

/**
 * The SVG transform that lays a TickShape along the scale: head pointing right, the rear
 * of the body on `startPx` and the tip of the mouthparts `lengthPx` further on, centred
 * on `cy`. Lining the body (not the leg span) up with the marks is what makes "3 mm"
 * mean three millimetre divisions.
 */
export function lyingTransform(
  startPx: number,
  lengthPx: number,
  cy: number,
  engorged: boolean,
): string {
  const k = lengthPx / bodyUnits(engorged);
  // rotate(90) sends the head (small y) to the right and the legs (x) up and down.
  return `translate(${round(startPx + lengthPx)} ${round(cy)}) rotate(90) scale(${round(k, 4)}) translate(-60 -${TICK_HEAD_Y})`;
}

/** How far the legs reach above and below the centre line, in px. */
export function legReachPx(lengthPx: number, engorged: boolean): number {
  return (TICK_HALF_SPAN * lengthPx) / bodyUnits(engorged);
}

/**
 * The magnification for the inset, or null when the tick is big enough to read at its
 * real size. Chooses the largest whole-number step that keeps the enlarged drawing near
 * LOUPE_TARGET_PX, so a pinhead shows as "×12" and a poppy seed as "×4".
 */
export function loupeMagnification(lengthPx: number): number | null {
  if (!(lengthPx > 0) || lengthPx >= LOUPE_BELOW_PX) return null;
  let best: number | null = null;
  for (const m of MAGNIFICATIONS) {
    if (lengthPx * m <= LOUPE_TARGET_PX * 1.1) best = m;
  }
  return best ?? MAGNIFICATIONS[0];
}

/**
 * Millimetre positions for the scale's numbered marks. Every 5 mm when there is room for
 * the numbers, every 10 mm when the screen is dense enough that they would collide.
 */
export function axisMarks(widthPx: number, pxPerMm: number, startPx = SCALE_START_PX): number[] {
  if (!(pxPerMm > 0) || !(widthPx > startPx)) return [];
  const step = 5 * pxPerMm >= 24 ? 5 : 10;
  const last = Math.floor((widthPx - startPx) / pxPerMm);
  const marks: number[] = [];
  for (let mm = 0; mm <= last; mm += step) marks.push(mm);
  return marks;
}

/** Height a scale row needs so the legs and the inset both fit, in px. */
export function rowHeightPx(lengthPx: number, engorged: boolean, minPx = 40): number {
  const legs = 2 * legReachPx(lengthPx, engorged) + 10;
  const loupe = loupeMagnification(lengthPx) ? 2 * LOUPE_RADIUS_PX + 6 : 0;
  return Math.ceil(Math.max(minPx, legs, loupe));
}

function round(n: number, places = 2): number {
  const f = 10 ** places;
  return Math.round(n * f) / f;
}
